import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { Plus, Save } from "lucide-react";
import ImageUploader from "../ImageUploader";
import BilingualField from "../BilingualField";

export default function ServicesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/services").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (editing.id) await api.put(`/services/${editing.id}`, editing);
    else await api.post("/services", editing);
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    await api.delete(`/services/${id}`);
    load();
  };

  const trFr = (v) => (typeof v === "string" ? v : v?.fr || v?.en || "");

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl">Services</h1>
          <p className="text-[#4A3F35] mt-2">Prestations affichées sur le site (FR + EN).</p>
        </div>
        <button onClick={() => setEditing({ number: "", title: { fr: "", en: "" }, short_description: { fr: "", en: "" }, full_description: { fr: "", en: "" }, image_url: "", order: items.length + 1 })} className="btn-primary" data-testid="add-service">
          <Plus size={16} /> Ajouter
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((s) => (
          <div key={s.id} className="bg-[#FDFBF7] rounded-2xl p-5 border border-[#2D2A26]/10 flex gap-4">
            {s.image_url && <img src={s.image_url} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#C87A5E]">{s.number}</div>
              <h3 className="font-serif text-xl truncate">{trFr(s.title)}</h3>
              <p className="text-sm text-[#4A3F35] mt-1 line-clamp-2">{trFr(s.short_description)}</p>
              <div className="flex gap-3 mt-3 text-sm">
                <button onClick={() => setEditing(s)} className="link-underline text-[#C87A5E]">Modifier</button>
                <button onClick={() => remove(s.id)} className="link-underline text-red-700">Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" onClick={() => setEditing(null)} data-testid="service-modal">
          <div className="bg-[#FDFBF7] rounded-3xl p-8 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">{editing.id ? "Modifier le service" : "Nouveau service"}</h3>
            <div className="space-y-4">
              <Input label="number" value={editing.number} onChange={(v) => setEditing({ ...editing, number: v })} />
              <BilingualField label="title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="service-title" />
              <ImageUploader value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} label="image_url" testid="service-image" />
              <BilingualField label="short_description" value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} type="textarea" testid="service-short" />
              <BilingualField label="full_description" value={editing.full_description} onChange={(v) => setEditing({ ...editing, full_description: v })} type="textarea" rows={5} testid="service-full" />
              <Input label="order" type="number" value={editing.order || 0} onChange={(v) => setEditing({ ...editing, order: Number(v) })} />
              <button onClick={save} className="btn-primary w-full justify-center" data-testid="service-save"><Save size={16} /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FDFBF7] rounded-3xl p-8 w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl mb-6">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export function Input({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-[#2D2A26]/15 rounded-xl px-4 py-2.5 text-sm focus:border-[#C87A5E] outline-none" />
    </div>
  );
}

export function Textarea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full border border-[#2D2A26]/15 rounded-xl px-4 py-3 text-sm focus:border-[#C87A5E] outline-none" />
    </div>
  );
}
